/* eslint-disable jsx-a11y/click-events-have-key-events */
import { groupBy } from 'ramda';
import type { MyWidgetsItem, MyWidgetsSection } from '@wix/add-panel-common';
import React, { FC, useRef } from 'react';
import { useMyWidgets } from '../../../../../../MyWidgetsProvider';
import { useActionsDispatcher } from '../../../../../../ActionsDispatcher';
import { SectionHeader } from '../../../../SectionHeader';
import styles from './MyWidgets.scss';
import type { MyWidgetsProps, PresetItemProps, Widget, WidgetPreset } from './MyWidgets.types';
import type { DragItemRect } from '../../../../../../..';
import { TextLabel } from '@wix/wix-base-ui';
import { WidgetThumb } from './WidgetThumb/WidgetThumb';

const mapWidgetItemsToPresets = (widgetItems: MyWidgetsItem[]): WidgetPreset[] =>
  widgetItems.map((item) => ({
    id: item.presetId ?? item.id,
    name: item.desc,
    image: item.image,
    size: item.size,
    onClick: item.onItemClick,
    onDrop: item.onItemDrop,
  }));

const appSectionToWidgets = (app: MyWidgetsSection): Widget[] => {
  const widgetItemsById = groupBy((item) => item.id, app.props.items);
  return Object.keys(widgetItemsById).map((id) => {
    const widgetItems = widgetItemsById[id];
    return {
      id,
      name: widgetItems[0]?.title,
      presets: mapWidgetItemsToPresets(widgetItems),
    };
  });
};

export const MyWidgets: FC<MyWidgetsProps> = () => {
  const { myWidgets: myWidgetsApps } = useMyWidgets();

  if (!myWidgetsApps || !myWidgetsApps.length) {
    return null;
  }

  return (
    <div data-hook="my-widgets">
      {myWidgetsApps.map((app) => (
        <div key={app.id} data-hook={`widget-app-${app.id}`}>
          {appSectionToWidgets(app).map((widget) => (
            <div key={widget.id} className={styles.app}>
              <SectionHeader
                headerText={widget.name}
                title={widget.name}
                disableInfoIcon
                shouldTranslate={false}
                dataHook={`widget-${widget.id}`}
              />

              <ul>
                {widget.presets.map((preset) => (
                  <li key={preset.id}>
                    <PresetItem preset={preset} shouldShowTitle={widget.presets.length > 1} />
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};

const PresetItem: FC<PresetItemProps> = ({ preset, shouldShowTitle }) => {
  const previewRef = useRef<HTMLDivElement>(null);
  const { dispatchStartMyWidgetDrag } = useActionsDispatcher();

  const handleMouseDown = (event: React.MouseEvent) => {
    if (!previewRef.current || !preset.size) {
      return;
    }
    const rect = getPreviewDimensions(previewRef.current);
    dispatchStartMyWidgetDrag({
      event,
      sectionName: preset.id,
      dragItemData: {
        item: {
          itemId: preset.id,
          onDrop: preset.onDrop,
          structure: {
            layout: preset.size,
          },
        },
        rect,
      },
    });
  };

  const getPreviewDimensions = (element: Element): DragItemRect => {
    const { left, top, width, height } = element.getBoundingClientRect();

    return { left, top, width, height };
  };

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      className={styles.presetItem}
      ref={previewRef}
      onMouseDown={handleMouseDown}
      onClick={preset.onClick}
      data-hook={`preset-item-${preset.id}`}
    >
      {shouldShowTitle && (
        <TextLabel
          value={preset.name}
          className={styles.presetItemName}
          enableEllipsis={true}
          shouldTranslate={false}
          type="T02"
        />
      )}
      <WidgetThumb thumb={preset.image} presetName={preset.name} />
    </div>
  );
};
